import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { serviceMaterialService } from '../../../services/supabaseService';
import { bunny } from '../../../lib/bunnyNet';

const EMPTY_FORM = { nom: '', prix: '', image_url: '' };

const formatPrice = (value) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(value) || 0);

const ServiceMaterialModal = ({ isOpen, onClose, service, onMaterialsChange }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const fileInputRef = useRef(null);

  const serviceId = service?.id;

  useEffect(() => {
    if (!isOpen || !serviceId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      const { data, error } = await serviceMaterialService.getByService(serviceId);
      if (cancelled) return;
      if (error) {
        console.error('Erreur chargement matériel:', error);
        setLoadError('Impossible de charger le matériel de ce service.');
        setMaterials([]);
      } else {
        setMaterials(data);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, serviceId]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setConfirmDeleteId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!imageFile) return undefined;
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setImageFile(null);
    setImagePreview('');
    setFormErrors({});
    setEditingId(null);
    setIsFormOpen(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const updateMaterials = (next) => {
    setMaterials(next);
    onMaterialsChange?.(serviceId, next);
  };

  const openCreate = () => {
    resetForm();
    setIsFormOpen(true);
  };

  const openEdit = (material) => {
    setForm({
      nom: material.nom || '',
      prix: material.prix != null ? String(material.prix) : '',
      image_url: material.image_url || '',
    });
    setImageFile(null);
    setImagePreview(material.image_url || '');
    setFormErrors({});
    setEditingId(material.id);
    setIsFormOpen(true);
    setConfirmDeleteId(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormErrors((prev) => ({ ...prev, image: 'Le fichier doit être une image.' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setFormErrors((prev) => ({ ...prev, image: "L'image ne doit pas dépasser 5 Mo." }));
      return;
    }
    setFormErrors((prev) => ({ ...prev, image: undefined }));
    setImageFile(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview('');
    setForm((prev) => ({ ...prev, image_url: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const validate = () => {
    const errors = {};
    if (!form.nom.trim()) errors.nom = 'Le nom est obligatoire.';
    const prix = `${form.prix}`.replace(',', '.').trim();
    if (prix === '') errors.prix = 'Le prix est obligatoire.';
    else if (Number.isNaN(Number(prix)) || Number(prix) < 0) errors.prix = 'Prix invalide.';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !serviceId) return;
    setSaving(true);
    try {
      let imageUrl = form.image_url || null;
      if (imageFile) {
        imageUrl = await bunny.uploadImage(imageFile);
      }
      const payload = {
        nom: form.nom.trim(),
        prix: Number(`${form.prix}`.replace(',', '.')),
        image_url: imageUrl,
      };

      if (editingId) {
        const { data, error } = await serviceMaterialService.update(editingId, payload);
        if (error) throw error;
        updateMaterials(materials.map((m) => (m.id === editingId ? data : m)));
      } else {
        const { data, error } = await serviceMaterialService.create({
          ...payload,
          service_id: serviceId,
          is_default: materials.length === 0,
        });
        if (error) throw error;
        updateMaterials([...materials, data]);
      }
      resetForm();
    } catch (err) {
      console.error('Erreur enregistrement matériel:', err);
      setFormErrors((prev) => ({ ...prev, global: "Erreur lors de l'enregistrement du matériel." }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (materialId) => {
    setDeletingId(materialId);
    const { error } = await serviceMaterialService.remove(materialId);
    setDeletingId(null);
    setConfirmDeleteId(null);
    if (error) {
      console.error('Erreur suppression matériel:', error);
      setLoadError('Erreur lors de la suppression du matériel.');
      return;
    }
    if (editingId === materialId) resetForm();
    updateMaterials(materials.filter((m) => m.id !== materialId));
  };

  const handleSetDefault = async (materialId) => {
    setSettingDefaultId(materialId);
    const { error } = await serviceMaterialService.setDefault(serviceId, materialId);
    setSettingDefaultId(null);
    if (error) {
      console.error('Erreur matériel par défaut:', error);
      setLoadError('Erreur lors du choix du matériel par défaut.');
      return;
    }
    setLoadError('');
    updateMaterials(materials.map((m) => ({ ...m, is_default: m.id === materialId })));
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-foreground truncate">
              Matériel du service « {service?.name || ''} »
            </h3>
            <p className="text-sm text-muted-foreground">
              {materials.length} matériel{materials.length > 1 ? 's' : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Fermer">
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loadError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 text-error text-sm">
              <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
              <span>{loadError}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Icon name="Loader" size={24} className="animate-spin mr-2" />
              Chargement du matériel...
            </div>
          ) : materials.length === 0 && !isFormOpen ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Icon name="Package" size={40} className="text-muted-foreground mb-3" />
              <p className="text-sm font-medium text-foreground">Aucun matériel pour ce service</p>
              <p className="text-sm text-muted-foreground mb-4">
                Ajoutez le matériel utilisé lors de l'intervention (nom, image et prix).
              </p>
              <Button iconName="Plus" iconPosition="left" iconSize={16} onClick={openCreate}>
                Ajouter un matériel
              </Button>
            </div>
          ) : (
            <ul className="divide-y divide-border border border-border rounded-lg">
              {materials.map((material) => (
                <li
                  key={material.id}
                  className={`flex items-center gap-3 p-3 ${material.is_default ? 'bg-primary/5' : ''}`}
                >
                  <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
                    {material.image_url ? (
                      <img src={material.image_url} alt={material.nom} className="w-full h-full object-cover" />
                    ) : (
                      <Icon name="Package" size={20} className="text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{material.nom}</p>
                      {material.is_default && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary whitespace-nowrap">
                          Par défaut
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{formatPrice(material.prix)}</p>
                  </div>
                  {confirmDeleteId === material.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Supprimer ?</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setConfirmDeleteId(null)}
                        disabled={deletingId === material.id}
                      >
                        Non
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(material.id)}
                        loading={deletingId === material.id}
                      >
                        Oui
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => !material.is_default && handleSetDefault(material.id)}
                        disabled={settingDefaultId !== null}
                        aria-pressed={!!material.is_default}
                        aria-label={
                          material.is_default
                            ? `${material.nom} est le matériel par défaut`
                            : `Définir ${material.nom} comme matériel par défaut`
                        }
                        title={material.is_default ? 'Matériel par défaut' : 'Définir par défaut'}
                      >
                        <Icon
                          name={settingDefaultId === material.id ? 'Loader' : 'Star'}
                          size={16}
                          className={
                            settingDefaultId === material.id
                              ? 'animate-spin text-muted-foreground'
                              : material.is_default
                                ? 'text-warning fill-current'
                                : 'text-muted-foreground'
                          }
                        />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(material)}
                        aria-label={`Modifier ${material.nom}`}
                      >
                        <Icon name="Pencil" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmDeleteId(material.id)}
                        aria-label={`Supprimer ${material.nom}`}
                      >
                        <Icon name="Trash2" size={16} className="text-error" />
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {isFormOpen && (
            <div className="border border-border rounded-lg p-4 bg-muted/30 space-y-4">
              <h4 className="text-sm font-semibold text-foreground">
                {editingId ? 'Modifier le matériel' : 'Nouveau matériel'}
              </h4>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px] gap-4">
                <Input
                  label="Nom"
                  required
                  value={form.nom}
                  onChange={(e) => setForm((prev) => ({ ...prev, nom: e.target.value }))}
                  error={formErrors.nom}
                  placeholder="Ex. Joint fibre 20x27"
                />
                <Input
                  label="Prix (€)"
                  required
                  type="text"
                  inputMode="decimal"
                  value={form.prix}
                  onChange={(e) => setForm((prev) => ({ ...prev, prix: e.target.value }))}
                  error={formErrors.prix}
                  placeholder="0,00"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-foreground mb-2">Image</span>
                <div className="flex items-center gap-3">
                  <div className="w-20 h-20 rounded-md bg-muted border border-border flex items-center justify-center overflow-hidden flex-shrink-0">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Aperçu" className="w-full h-full object-cover" />
                    ) : (
                      <Icon name="Image" size={24} className="text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      id="service-material-image"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      iconPosition="left"
                      iconSize={14}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {imagePreview ? "Changer l'image" : 'Choisir une image'}
                    </Button>
                    {imagePreview && (
                      <Button variant="ghost" size="sm" onClick={handleRemoveImage}>
                        Retirer l'image
                      </Button>
                    )}
                  </div>
                </div>
                {formErrors.image && <p className="text-sm text-error mt-1">{formErrors.image}</p>}
              </div>

              {formErrors.global && <p className="text-sm text-error">{formErrors.global}</p>}

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={resetForm} disabled={saving}>
                  Annuler
                </Button>
                <Button onClick={handleSave} loading={saving}>
                  {editingId ? 'Enregistrer' : 'Ajouter'}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-border">
          {!isFormOpen && materials.length > 0 ? (
            <Button variant="outline" iconName="Plus" iconPosition="left" iconSize={16} onClick={openCreate}>
              Ajouter un matériel
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={onClose}>Fermer</Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ServiceMaterialModal;
